/**
 * Sección plegable reutilizable.
 *
 * Envuelve el contenido en una Card con un header clickeable que
 * abre/cierra el body. El estado se persiste en localStorage bajo
 * `persistKey` para que la preferencia sobreviva reloads.
 *
 * Regla de default:
 *   - `defaultOpen` sólo aplica la primera vez (sin estado persistido).
 *   - Si ya hay valor en localStorage, ese gana.
 *
 * Vive en `@atreus/ui` para compartirlo entre desktop y admin.
 */

import { ChevronDown } from 'lucide-react';
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from './card.js';

interface Props {
  /** Título en el header clickeable. */
  title: string;
  /** Icono opcional a la izquierda del título. */
  icon?: ComponentType<{ className?: string }> | undefined;
  /** Texto secundario bajo el título cuando está abierto. */
  description?: string | undefined;
  /** Clave estable para persistir el estado en localStorage. */
  persistKey: string;
  /** Estado inicial cuando no hay nada persistido aún. */
  defaultOpen?: boolean | undefined;
  children: ReactNode;
}

const storageKey = (key: string): string => `atreus.ui.collapsible.${key}`;

export const CollapsibleSection = ({
  title,
  icon: Icon,
  description,
  persistKey,
  defaultOpen = false,
  children,
}: Props) => {
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return defaultOpen;
    const stored = window.localStorage.getItem(storageKey(persistKey));
    if (stored === 'open') return true;
    if (stored === 'closed') return false;
    return defaultOpen;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      storageKey(persistKey),
      open ? 'open' : 'closed',
    );
  }, [persistKey, open]);

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 rounded-t-md px-6 py-4 text-left transition-colors hover:bg-surface-muted"
        aria-expanded={open}
      >
        {Icon ? (
          <span className="flex size-8 items-center justify-center rounded-md bg-fg text-fg-inverse">
            <Icon className="size-4" />
          </span>
        ) : null}
        <CardHeader className="flex-1 p-0">
          <CardTitle className="text-base">{title}</CardTitle>
          {description && open ? (
            <p className="mt-1 text-xs text-fg-muted">{description}</p>
          ) : null}
        </CardHeader>
        <ChevronDown
          className={
            'size-5 shrink-0 text-fg-muted transition-transform ' +
            (open ? 'rotate-180' : '')
          }
        />
      </button>
      {open ? (
        <CardContent className="border-t border-border pt-4">
          {children}
        </CardContent>
      ) : null}
    </Card>
  );
};
